import { LayoutArea } from '../types';

/**
 * Layout Strategy System - Automatically detects and implements common layout patterns
 * Based on which panels are present, chooses the optimal nested structure
 */

export enum LayoutPattern {
  // Simple patterns
  VERTICAL_STACK = 'vertical-stack', // Header → Content → Footer
  HORIZONTAL_STACK = 'horizontal-stack', // Sidebar ← → Content ← → Sidebar

  // Nested patterns
  HEADER_HORIZONTAL_FOOTER = 'header-horizontal-footer', // Header → (Sidebar + Content) → Footer
  SIDEBAR_VERTICAL_MAIN = 'sidebar-vertical-main', // (Sidebar) + (Header → Content → Footer)
  MULTI_SIDEBAR_MAIN = 'multi-sidebar-main', // (Nav + Sidebar) + (Header → Content → Footer)
}

export interface LayoutStructure {
  pattern: LayoutPattern;
  primaryDirection: 'horizontal' | 'vertical';
  nestedGroups: {
    id: string;
    direction: 'horizontal' | 'vertical';
    panels: LayoutArea[];
    order: number;
    isNested?: boolean;
  }[];
  spanningPanels: LayoutArea[]; // Panels that span the full height/width
}

/**
 * Empirical layout analyzer - uses panel presence to determine optimal structure
 */
export class LayoutStrategyAnalyzer {
  /**
   * Analyze visible panels and determine the best layout structure
   */
  static analyzeLayout(visiblePanels: LayoutArea[]): LayoutStructure {
    const hasHeader = visiblePanels.includes(LayoutArea.HEADER);
    const hasFooter = visiblePanels.includes(LayoutArea.FOOTER);
    const hasLeftSidebar = visiblePanels.includes(LayoutArea.LEFT_SIDEBAR);
    const hasRightSidebar = visiblePanels.includes(LayoutArea.RIGHT_SIDEBAR);
    const hasContent = visiblePanels.includes(LayoutArea.CONTENT);

    // Pattern 1: Simple vertical stack (Header → Content → Footer)
    if ((hasHeader || hasFooter) && !hasLeftSidebar && !hasRightSidebar) {
      return this.createVerticalStackLayout(visiblePanels);
    }

    // Pattern 2: Simple horizontal stack (Sidebar ← → Content ← → Sidebar)
    if ((hasLeftSidebar || hasRightSidebar) && !hasHeader && !hasFooter) {
      return this.createHorizontalStackLayout(visiblePanels);
    }

    // Pattern 3: Header + Horizontal + Footer (Header → (Sidebar + Content) → Footer)
    if ((hasHeader || hasFooter) && (hasLeftSidebar || hasRightSidebar)) {
      return this.createHeaderHorizontalFooterLayout(visiblePanels);
    }

    // Pattern 4: Full-height sidebar + Vertical main (Sidebar) + (Header → Content → Footer)
    // This pattern is chosen when we want sidebars to span full height
    if (hasLeftSidebar && (hasHeader || hasFooter) && this.shouldUseFullHeightSidebar(visiblePanels)) {
      return this.createSidebarVerticalMainLayout(visiblePanels);
    }

    // Pattern 5: Multi-sidebar + Vertical (Nav + Sidebar) + (Header → Content → Footer)
    if (hasLeftSidebar && hasRightSidebar && (hasHeader || hasFooter)) {
      return this.createMultiSidebarMainLayout(visiblePanels);
    }

    // Fallback: Default to vertical stack
    return this.createVerticalStackLayout(visiblePanels);
  }

  /**
   * Determine if sidebars should span full height based on content density
   */
  private static shouldUseFullHeightSidebar(visiblePanels: LayoutArea[]): boolean {
    // Use full-height sidebar when:
    // 1. Multiple navigation elements are present
    // 2. Sidebar content is primary navigation
    // 3. Header/footer are lightweight (status/branding only)

    const hasMultipleVerticalElements = [LayoutArea.HEADER, LayoutArea.FOOTER].filter((area) => visiblePanels.includes(area)).length >= 2;

    return hasMultipleVerticalElements;
  }

  /**
   * Pattern 1: Header → Content → Footer
   */
  private static createVerticalStackLayout(visiblePanels: LayoutArea[]): LayoutStructure {
    return {
      pattern: LayoutPattern.VERTICAL_STACK,
      primaryDirection: 'vertical',
      nestedGroups: [
        {
          id: 'main-vertical',
          direction: 'vertical',
          panels: visiblePanels.filter((p) => [LayoutArea.HEADER, LayoutArea.CONTENT, LayoutArea.FOOTER].includes(p)),
          order: 1,
        },
      ],
      spanningPanels: [],
    };
  }

  /**
   * Pattern 2: Sidebar ← → Content ← → Sidebar
   */
  private static createHorizontalStackLayout(visiblePanels: LayoutArea[]): LayoutStructure {
    return {
      pattern: LayoutPattern.HORIZONTAL_STACK,
      primaryDirection: 'horizontal',
      nestedGroups: [
        {
          id: 'main-horizontal',
          direction: 'horizontal',
          panels: visiblePanels.filter((p) => [LayoutArea.LEFT_SIDEBAR, LayoutArea.CONTENT, LayoutArea.RIGHT_SIDEBAR].includes(p)),
          order: 1,
        },
      ],
      spanningPanels: [],
    };
  }

  /**
   * Pattern 3: Header → (Sidebar + Content + Sidebar) → Footer
   */
  private static createHeaderHorizontalFooterLayout(visiblePanels: LayoutArea[]): LayoutStructure {
    const groups = [];
    let order = 1;

    // Header group (if present)
    if (visiblePanels.includes(LayoutArea.HEADER)) {
      groups.push({
        id: 'header-group',
        direction: 'vertical' as const,
        panels: [LayoutArea.HEADER],
        order: order++,
      });
    }

    // Main horizontal content group
    const horizontalPanels = visiblePanels.filter((p) => [LayoutArea.LEFT_SIDEBAR, LayoutArea.CONTENT, LayoutArea.RIGHT_SIDEBAR].includes(p));

    if (horizontalPanels.length > 0) {
      groups.push({
        id: 'main-horizontal',
        direction: 'horizontal' as const,
        panels: horizontalPanels,
        order: order++,
        isNested: true,
      });
    }

    // Footer group (if present)
    if (visiblePanels.includes(LayoutArea.FOOTER)) {
      groups.push({
        id: 'footer-group',
        direction: 'vertical' as const,
        panels: [LayoutArea.FOOTER],
        order: order++,
      });
    }

    return {
      pattern: LayoutPattern.HEADER_HORIZONTAL_FOOTER,
      primaryDirection: 'vertical',
      nestedGroups: groups,
      spanningPanels: [],
    };
  }

  /**
   * Pattern 4: (Sidebar) + (Header → Content → Footer)
   */
  private static createSidebarVerticalMainLayout(visiblePanels: LayoutArea[]): LayoutStructure {
    const groups = [];
    let order = 1;

    // Left sidebar (spanning full height)
    if (visiblePanels.includes(LayoutArea.LEFT_SIDEBAR)) {
      groups.push({
        id: 'left-sidebar',
        direction: 'horizontal' as const,
        panels: [LayoutArea.LEFT_SIDEBAR],
        order: order++,
      });
    }

    // Main vertical content area
    const verticalPanels = visiblePanels.filter((p) => [LayoutArea.HEADER, LayoutArea.CONTENT, LayoutArea.FOOTER].includes(p));

    if (verticalPanels.length > 0) {
      groups.push({
        id: 'main-vertical',
        direction: 'vertical' as const,
        panels: verticalPanels,
        order: order++,
        isNested: true,
      });
    }

    // Right sidebar (if present, also spanning full height)
    if (visiblePanels.includes(LayoutArea.RIGHT_SIDEBAR)) {
      groups.push({
        id: 'right-sidebar',
        direction: 'horizontal' as const,
        panels: [LayoutArea.RIGHT_SIDEBAR],
        order: order++,
      });
    }

    return {
      pattern: LayoutPattern.SIDEBAR_VERTICAL_MAIN,
      primaryDirection: 'horizontal',
      nestedGroups: groups,
      spanningPanels: [LayoutArea.LEFT_SIDEBAR, LayoutArea.RIGHT_SIDEBAR].filter((p) => visiblePanels.includes(p)),
    };
  }

  /**
   * Pattern 5: (Nav + Sidebar) + (Header → Content → Footer)
   * For complex navigation scenarios with multiple left panels
   */
  private static createMultiSidebarMainLayout(visiblePanels: LayoutArea[]): LayoutStructure {
    const groups = [];
    let order = 1;

    // Multiple left panels (nav + sidebar)
    const leftPanels = visiblePanels.filter((p) => p === LayoutArea.LEFT_SIDEBAR);
    if (leftPanels.length > 0) {
      groups.push({
        id: 'left-panel-group',
        direction: 'horizontal' as const,
        panels: leftPanels,
        order: order++,
      });
    }

    // Main vertical content area
    const verticalPanels = visiblePanels.filter((p) => [LayoutArea.HEADER, LayoutArea.CONTENT, LayoutArea.FOOTER].includes(p));

    if (verticalPanels.length > 0) {
      groups.push({
        id: 'main-vertical',
        direction: 'vertical' as const,
        panels: verticalPanels,
        order: order++,
        isNested: true,
      });
    }

    // Right panels
    if (visiblePanels.includes(LayoutArea.RIGHT_SIDEBAR)) {
      groups.push({
        id: 'right-sidebar',
        direction: 'horizontal' as const,
        panels: [LayoutArea.RIGHT_SIDEBAR],
        order: order++,
      });
    }

    return {
      pattern: LayoutPattern.MULTI_SIDEBAR_MAIN,
      primaryDirection: 'horizontal',
      nestedGroups: groups,
      spanningPanels: [LayoutArea.LEFT_SIDEBAR, LayoutArea.RIGHT_SIDEBAR].filter((p) => visiblePanels.includes(p)),
    };
  }
}

/**
 * Layout metrics calculator using react-resizable-panels empirical data
 */
export class LayoutMetricsCalculator {
  /**
   * Calculate optimal sizes for a layout structure using empirical viewport data
   */
  static calculateLayoutMetrics(
    structure: LayoutStructure,
    viewport: { width: number; height: number },
    panelConfigs: Record<LayoutArea, any>,
  ): Record<string, { panels: LayoutArea[]; sizes: number[] }> {
    const results: Record<string, { panels: LayoutArea[]; sizes: number[] }> = {};

    structure.nestedGroups.forEach((group) => {
      const sizes = this.calculateGroupSizes(group.panels, group.direction, viewport, panelConfigs, structure.spanningPanels.includes(group.panels[0]));

      results[group.id] = {
        panels: group.panels,
        sizes,
      };
    });

    return results;
  }

  /**
   * Calculate sizes for a specific panel group using empirical measurements
   */
  private static calculateGroupSizes(
    panels: LayoutArea[],
    direction: 'horizontal' | 'vertical',
    viewport: { width: number; height: number },
    panelConfigs: Record<LayoutArea, any>,
    isSpanning: boolean = false,
  ): number[] {
    const totalSpace = direction === 'horizontal' ? viewport.width : viewport.height;
    const sizes: number[] = [];

    let remainingSpace = 100; // Percentage
    let fixedSpaceUsed = 0;

    // First pass: calculate fixed-size panels
    panels.forEach((panel) => {
      const config = panelConfigs[panel] || {};

      if (config.autoSize === 'fixed' && config.minPixels) {
        const pixelPercent = Math.round((config.minPixels / totalSpace) * 100);
        fixedSpaceUsed += pixelPercent;
      }
    });

    remainingSpace -= fixedSpaceUsed;

    // Second pass: distribute remaining space
    let flexPanelCount = 0;
    const flexPanels: LayoutArea[] = [];

    panels.forEach((panel) => {
      const config = panelConfigs[panel] || {};

      if (config.autoSize !== 'fixed') {
        flexPanelCount++;
        flexPanels.push(panel);
      }
    });

    // Calculate final sizes
    panels.forEach((panel) => {
      const config = panelConfigs[panel] || {};

      if (config.autoSize === 'fixed' && config.minPixels) {
        const pixelPercent = Math.round((config.minPixels / totalSpace) * 100);
        sizes.push(Math.max(5, Math.min(50, pixelPercent))); // Constrain to reasonable bounds
      } else if (panel === LayoutArea.CONTENT) {
        // Content gets priority for remaining space
        const contentSize = Math.max(30, remainingSpace - (flexPanelCount - 1) * 20);
        sizes.push(contentSize);
        remainingSpace -= contentSize;
        flexPanelCount--;
      } else {
        // Distribute remaining space among other flex panels
        const panelSize = flexPanelCount > 0 ? remainingSpace / flexPanelCount : 20;
        sizes.push(Math.max(10, Math.min(40, panelSize)));
        remainingSpace -= panelSize;
        flexPanelCount--;
      }
    });

    return sizes;
  }
}

/**
 * Developer-friendly layout configuration
 */
export interface AutoLayoutConfig {
  /** Force a specific layout pattern (optional - auto-detected by default) */
  forcePattern?: LayoutPattern;

  /** Prefer full-height sidebars when possible */
  preferFullHeightSidebars?: boolean;

  /** Custom sizing priorities for panels */
  panelPriorities?: Partial<Record<LayoutArea, number>>;

  /** Responsive behavior overrides */
  responsiveOverrides?: {
    mobile?: LayoutPattern;
    tablet?: LayoutPattern;
    desktop?: LayoutPattern;
  };
}

/**
 * Main layout strategy coordinator
 */
export class AutoLayoutStrategy {
  static planLayout(
    visiblePanels: LayoutArea[],
    config: AutoLayoutConfig = {},
    viewport: { width: number; height: number },
  ): {
    structure: LayoutStructure;
    metrics: Record<string, { panels: LayoutArea[]; sizes: number[] }>;
    debugInfo: {
      detectedPattern: LayoutPattern;
      reason: string;
      alternatives: LayoutPattern[];
    };
  } {
    // Apply responsive overrides if specified
    const effectivePattern = this.getResponsivePattern(config, viewport.width);

    // Analyze layout structure
    const structure = effectivePattern ? this.forcePattern(visiblePanels, effectivePattern) : LayoutStrategyAnalyzer.analyzeLayout(visiblePanels);

    // Calculate empirical metrics
    const metrics = LayoutMetricsCalculator.calculateLayoutMetrics(
      structure,
      viewport,
      Object.fromEntries(Object.values(LayoutArea).map((area) => [area, {}])) as Record<LayoutArea, any>, // Panel configs would be passed from the component
    );

    // Generate debug information
    const debugInfo = {
      detectedPattern: structure.pattern,
      reason: this.getPatternReason(structure.pattern, visiblePanels),
      alternatives: this.getAlternativePatterns(visiblePanels),
    };

    return { structure, metrics, debugInfo };
  }

  private static getResponsivePattern(config: AutoLayoutConfig, width: number): LayoutPattern | undefined {
    if (!config.responsiveOverrides) return config.forcePattern;

    if (width <= 768 && config.responsiveOverrides.mobile) {
      return config.responsiveOverrides.mobile;
    } else if (width <= 1024 && config.responsiveOverrides.tablet) {
      return config.responsiveOverrides.tablet;
    } else if (config.responsiveOverrides.desktop) {
      return config.responsiveOverrides.desktop;
    }

    return config.forcePattern;
  }

  private static forcePattern(visiblePanels: LayoutArea[], pattern: LayoutPattern): LayoutStructure {
    // Implementation would force the specific pattern
    // For now, fall back to auto-detection
    return LayoutStrategyAnalyzer.analyzeLayout(visiblePanels);
  }

  private static getPatternReason(pattern: LayoutPattern, panels: LayoutArea[]): string {
    switch (pattern) {
      case LayoutPattern.VERTICAL_STACK:
        return 'Simple vertical layout - only header/content/footer present';
      case LayoutPattern.HORIZONTAL_STACK:
        return 'Simple horizontal layout - only sidebars and content present';
      case LayoutPattern.HEADER_HORIZONTAL_FOOTER:
        return 'Nested layout - header/footer with horizontal content area';
      case LayoutPattern.SIDEBAR_VERTICAL_MAIN:
        return 'Full-height sidebar layout - sidebar spans entire height';
      case LayoutPattern.MULTI_SIDEBAR_MAIN:
        return 'Complex navigation layout - multiple sidebars with main content area';
      default:
        return 'Auto-detected based on panel composition';
    }
  }

  private static getAlternativePatterns(panels: LayoutArea[]): LayoutPattern[] {
    const alternatives: LayoutPattern[] = [];

    // Logic to suggest alternative patterns based on current panels
    if (panels.includes(LayoutArea.LEFT_SIDEBAR)) {
      alternatives.push(LayoutPattern.SIDEBAR_VERTICAL_MAIN);
      alternatives.push(LayoutPattern.HEADER_HORIZONTAL_FOOTER);
    }

    return alternatives;
  }
}
